<template>
  <div class="chunk-row">
    <div class="icon-col">
      <div v-if="isConnected" class="connector-line" />
      <template v-if="isError">
        <span class="chunk-status-icon chunk-status-icon--error">
          <BbIcon size="small" name="x-small" />
        </span>
      </template>
      <template v-else-if="isLoading">
        <div class="loading-wrapper">
          <span class="chunk-status-icon chunk-status-icon--loading" />
        </div>
      </template>
      <template v-else>
        <span class="chunk-status-icon chunk-status-icon--done">
          <BbIcon size="small" name="checkmark-small" />
        </span>
      </template>
    </div>

    <template v-if="isError">
      <div
        class="markdown-content markdown-content--thinking tool-error-text"
        v-html="formatContent(event.data.message || inputSummary)"
      />
    </template>
    <template v-else>
      <div :class="contentClass" v-html="formatContent(inputSummary)" />
      <div v-if="event.data?.output" :class="contentClass" v-html="formatContent(toolResultSummary)" />
      <div v-if="event.data?.message" :class="contentClass" v-html="formatContent(event.data.message)" />
      <div v-if="Array.isArray(event.data?.items) && event.data.items.length" class="tool-items-block">
        <div class="tool-items-title">Items:</div>
        <ul class="tool-items-list">
          <li v-for="(item, idx) in event.data.items" :key="idx" class="tool-item-row">
            <pre>{{ JSON.stringify(item, null, 2) }}</pre>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';

import { TOOL_CALL_TYPE } from '~/constants/conversationModels';
import { formatKey } from '~/utils/format';

const props = defineProps({
  event: { type: Object, required: true },
  isLoading: { type: Boolean, default: false },
  isConnected: { type: Boolean, default: false },
  formatContent: { type: Function, required: true },
});

const isError = computed(() => props.event.data.event === TOOL_CALL_TYPE.TOOL_ERROR);

const inputSummary = computed(() => {
  const input = props.event.data?.input;
  if (!input || typeof input !== 'object' || Array.isArray(input)) return 'Called';
  const entries = Object.entries(input);
  if (!entries.length) return 'Called';
  return entries.map(([k, v]) => `**${formatKey(k)}:** ${typeof v === 'object' ? JSON.stringify(v) : v}`).join('\n\n');
});

const toolResultSummary = computed(() => {
  const artifact = props.event.data?.output?.artifact;
  if (artifact === undefined || artifact === null) return 'Success';
  if (Array.isArray(artifact)) return `${artifact.length} results`;
  if (typeof artifact === 'object') return `${Object.keys(artifact).length} fields`;
  return `Result: ${artifact}`;
});

const contentClass = computed(() => [
  'markdown-content',
  'markdown-content--thinking',
  props.isLoading ? 'tool-step-label tool-step-label--running' : 'tool-step-label tool-step-label--idle',
]);
</script>

<style scoped lang="scss">
@keyframes chunk-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes step-sweep {
  from {
    background-position: 200% center;
  }
  to {
    background-position: -200% center;
  }
}

.chunk-row {
  display: flex;
  align-items: flex-start;
  gap: $bb-spacing-2;
  min-height: 12px;

  .icon-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: stretch;
    justify-content: flex-start;
    flex-shrink: 0;

    .connector-line {
      order: 2;
      flex: 1 1 auto;
      width: 1px;
      background: $bb-neutral-4;
      margin-bottom: calc(-1 * var(--tool-step-gap, 12px));
    }
    .chunk-status-icon,
    .loading-wrapper {
      order: 1;
      z-index: 1;
    }
  }

  .loading-wrapper {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border: 1px solid $bb-neutral-4;
    border-radius: 50%;
  }
}

.chunk-status-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
.chunk-status-icon--loading {
  width: 8px;
  height: 8px;
  border: 2px solid transparent;
  border-top-color: $bb-primary-1;
  border-right-color: $bb-primary-1;
  animation: chunk-spin 0.75s linear infinite;
}
.chunk-status-icon--done {
  width: 16px;
  height: 16px;
  background-color: #000;
  color: $bb-neutral-0 !important;
}
.chunk-status-icon--error {
  width: 16px;
  height: 16px;
  background: #fee2e2;
  color: $bb-interface-5 !important;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.markdown-content--thinking {
  font-size: 13px !important;
  margin-top: -2px;
  color: #6a6765;
  &.tool-error-text {
    color: $bb-interface-5 !important;
  }
}
.tool-error-text {
  color: $bb-interface-5 !important;
}

.tool-step-label--idle {
  color: #6a6765 !important;
}
.tool-step-label--running {
  font-weight: 700;
  background: linear-gradient(90deg, $bb-neutral-1 30%, #c0c0c0 50%, $bb-neutral-1 70%) 200% center / 300% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: step-sweep 2s linear infinite;
}

:deep(.markdown-content) {
  p {
    margin: 0 !important;
  }
  > *:first-child {
    margin-top: 0 !important;
  }
  > *:last-child {
    margin-bottom: 0 !important;
  }
}
:deep(.tool-step-token) {
  font-weight: 700;
}

.tool-items-block {
  background: #f7f7fa;
  border-radius: 8px;
  padding: 8px 12px;
  margin: 8px 0;
  .tool-items-title {
    font-weight: 600;
    margin-bottom: 4px;
    color: #4b4b4b;
    font-size: 13px;
  }
  .tool-items-list {
    list-style: disc inside;
    margin: 0;
    padding: 0 0 0 12px;
  }
  .tool-item-row {
    font-size: 12px;
    color: #333;
    margin-bottom: 2px;
  }
}
</style>
